import React, { useMemo } from 'react';
import { Avatar, Box, Tooltip } from '@mui/material';
import { CameraAlt } from '@mui/icons-material';
import { getVendorInfo, normalizeWikimediaThumbUrl } from '../utils/vendorLogos';
import { useBrandRegistry } from '../hooks/useBrandRegistry';

interface VendorLogoProps {
  brand: string;
  /** When provided (e.g. from API), use this logo URL so onboarding/settings show correct logos */
  logoUrl?: string | null;
  size?: number;
  isDataIngestion?: boolean;
  showTooltip?: boolean;
}

export const VendorLogo: React.FC<VendorLogoProps> = ({ 
  brand, 
  logoUrl: logoUrlProp,
  size = 40, 
  isDataIngestion = false, 
  showTooltip = true 
}) => {
  const { data: brandRegistry } = useBrandRegistry();
  const vendorInfo = getVendorInfo(brand, isDataIngestion);

  const registryBrand = useMemo(() => {
    if (!brandRegistry || !brand) return null;
    const normalized = String(brand).toLowerCase().replace(/[^a-z0-9]+/g, '-');
    return (
      brandRegistry.find(item => item.slug === normalized) ||
      brandRegistry.find(item => item.name.toLowerCase() === String(brand).toLowerCase())
    );
  }, [brandRegistry, brand]);

  // Prefer: local asset (vendorInfo.logo), then API/parent logoUrl, then registry
  const logoUrl = normalizeWikimediaThumbUrl(
    vendorInfo.logo || logoUrlProp || registryBrand?.logoUrl || '',
  );
  const displayName = registryBrand?.name || vendorInfo.name;
  
  const logoContent = (
    <Avatar
      sx={{
        width: size,
        height: size,
        bgcolor: 'white',
        border: '2px solid',
        borderColor: vendorInfo.color,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}
    >
      {logoUrl ? (
        <Box
          component="img"
          src={logoUrl}
          alt={`${displayName} logo`}
          sx={{
            width: '90%',
            height: '90%',
            objectFit: 'contain',
            padding: '2px',
          }}
          onError={(e) => {
            // Fallback to icon if image fails to load
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent) {
              const icon = document.createElement('div');
              icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
              parent.appendChild(icon);
            }
          }}
        />
      ) : (
        <CameraAlt sx={{ fontSize: size * 0.6, color: vendorInfo.color }} />
      )}
    </Avatar>
  );

  if (showTooltip) {
    return (
      <Tooltip title={displayName} arrow>
        {logoContent}
      </Tooltip>
    );
  }

  return logoContent;
};
